import React, { createContext, useContext, useEffect, useState } from "react";
import supabase from "../utils/supabase";
import { useAuth } from "./AuthContext";
import organizationSeedingService from "../utils/organizationSeedingService";

const OrganizationContext = createContext();

export const OrganizationProvider = ({ children }) => {
  const { user, loading: authLoading } = useAuth();
  const [memberships, setMemberships] = useState([]);
  const [currentOrg, setCurrentOrg] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setMemberships([]);
      setCurrentOrg(null);
      setLoading(false);
      return;
    }

    let isMounted = true;
    const fetchMemberships = async () => {
      setLoading(true);
      
      const { data, error } = await supabase
        .from("organization_members")
        .select(`organization_id, role, organizations(id, name, business_name, logo_url, google_review_url, yelp_url, facebook_url, plan_tier, business_type, timezone, business_address, business_phone, business_hours, service_areas, team_size, primary_review_platform, created_at, updated_at)`)
        .eq("user_id", user.id);

      if (isMounted) {
        if (error) {
          console.error("Failed to fetch organization memberships", error);
          setMemberships([]);
        } else {
          setMemberships(data);
          if (!currentOrg && data && data.length) {
            setCurrentOrg(data[0].organizations);
          }
        }
        setLoading(false);
      }
    };

    fetchMemberships();

    return () => {
      isMounted = false;
    };
  }, [user, authLoading]);

  const updateOrganization = async (updates) => {
    if (!currentOrg) return { success: false, error: 'No organization selected' };
    
    try {
      const { data, error } = await supabase
        .from('organizations')
        .update(updates)
        .eq('id', currentOrg.id)
        .select()
        .single();

      if (error) {
        console.error('❌ OrganizationContext: Database error:', error);
        return { success: false, error: error.message };
      }

      // Update the current organization state
      setCurrentOrg(data);
      
      // Also update the organization in the memberships array
      setMemberships(prev => prev.map(membership => 
        membership.organizations.id === data.id 
          ? { ...membership, organizations: data }
          : membership
      ));
      
      // If this is the first time the organization is being set up (business_type is being set)
      // and it's during onboarding, seed default lead sources
      if (updates.business_type && !currentOrg.business_type && user) {
        try {
          console.log('🌱 Seeding default lead sources for organization setup...');
          await organizationSeedingService.addMissingDefaultLeadSources(currentOrg.id, user.id);
        } catch (seedError) {
          console.error('Failed to seed default lead sources:', seedError);
          // Don't fail the organization update if seeding fails
        }
      }
      
      return { success: true, data };
    } catch (error) {
      console.error('💥 OrganizationContext: Exception during update:', error);
      return { success: false, error: 'Failed to update organization' };
    }
  };

  const value = {
    memberships,
    currentOrg,
    currentOrganization: currentOrg,
    setCurrentOrg,
    updateOrganization,
    loading,
  };

  return (
    <OrganizationContext.Provider value={value}>
      {children}
    </OrganizationContext.Provider>
  );
};

export const useOrganization = () => useContext(OrganizationContext); 